import React from 'react';
import { cn } from '@/lib/utils';
import {
    Calendar,
    CheckCircle2,
    Clock,
    FileText,
    MessageSquare,
    XCircle,
    AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import type { AdoptionApplicationTimeline, TimelineEventType } from '../types';

interface TimelineProps {
    events: AdoptionApplicationTimeline[];
    className?: string;
}

export function Timeline({ events, className }: TimelineProps) {
    if (!events.length) {
        return (
            <div className="text-center py-8 text-muted-foreground">
                No hay eventos registrados aún.
            </div>
        );
    }

    // Ordenar eventos del más reciente al más antiguo
    const sortedEvents = [...events].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return (
        <div className={cn("space-y-8", className)}>
            {sortedEvents.map((event, index) => {
                const isLast = index === sortedEvents.length - 1;
                const Icon = getEventIcon(event.eventType);
                const colorClass = getEventColor(event.eventType);

                return (
                    <div key={event.id} className="relative flex gap-4">
                        {/* Línea conectora */}
                        {!isLast && (
                            <div
                                className="absolute left-[19px] top-10 bottom-[-32px] w-0.5 bg-border"
                                aria-hidden="true"
                            />
                        )}

                        {/* Icono */}
                        <div className={cn(
                            "relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-background",
                            colorClass
                        )}>
                            <Icon className="h-5 w-5" />
                        </div>

                        {/* Contenido */}
                        <div className="flex-1 pt-1.5">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
                                <h4 className="font-semibold text-foreground">
                                    {getEventTitle(event)}
                                </h4>
                                <time className="text-xs text-muted-foreground whitespace-nowrap flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {format(new Date(event.createdAt), 'dd/MM/yyyy HH:mm')}
                                </time>
                            </div>

                            <p className="mt-2 text-sm text-muted-foreground">
                                {event.description}
                            </p>
                            {(event.status || event.createdByName) && (
                                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                    {event.status && (
                                        <span>
                                            <strong className="font-medium text-foreground">Estado:</strong> {statusLabel(event.status)}
                                        </span>
                                    )}
                                    {event.createdByName && (
                                        <span>
                                            <strong className="font-medium text-foreground">Usuario:</strong> {event.createdByName}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function getEventIcon(type: TimelineEventType) {
    switch (type) {
        case 'CREATED':
            return FileText;
        case 'STATUS_CHANGED':
            return CheckCircle2;
        case 'NOTE_ADDED':
            return MessageSquare;
        case 'INTERVIEW_SCHEDULED':
            return Calendar;
        case 'VISIT_SCHEDULED':
            return Calendar;
        case 'CONTRACT_SIGNED':
            return FileText;
        default:
            return AlertCircle;
    }
}

function getEventColor(type: TimelineEventType) {
    switch (type) {
        case 'CREATED':
            return "border-blue-200 text-blue-600 bg-blue-50";
        case 'STATUS_CHANGED':
            return "border-green-200 text-green-600 bg-green-50";
        case 'NOTE_ADDED':
            return "border-gray-200 text-gray-600 bg-gray-50";
        case 'INTERVIEW_SCHEDULED':
        case 'VISIT_SCHEDULED':
            return "border-purple-200 text-purple-600 bg-purple-50";
        case 'CONTRACT_SIGNED':
            return "border-yellow-200 text-yellow-600 bg-yellow-50";
        default:
            return "border-gray-200 text-gray-600";
    }
}

function getEventTitle(event: AdoptionApplicationTimeline) {
    switch (event.eventType) {
        case 'CREATED':
            return event.status ? `Solicitud creada: ${statusLabel(event.status)}` : 'Solicitud creada';
        case 'STATUS_CHANGED':
            return event.status ? `Cambio de estado: ${statusLabel(event.status)}` : 'Cambio de estado';
        case 'NOTE_ADDED':
            return 'Nota agregada';
        case 'INTERVIEW_SCHEDULED':
            return 'Entrevista agendada';
        case 'VISIT_SCHEDULED':
            return 'Visita agendada';
        case 'CONTRACT_SIGNED':
            return 'Contrato firmado';
        default:
            return 'Evento registrado';
    }
}

function statusLabel(status?: string | null) {
    const labels: Record<string, string> = {
        SUBMITTED: 'Enviada',
        IN_REVIEW: 'En revisión',
        APPROVED: 'Aprobada',
        REJECTED: 'Rechazada',
        CANCELLED: 'Cancelada',
    };
    const key = String(status || '').toUpperCase();
    return labels[key] || key || 'Actualizada';
}
